import React from "react";
import { Box, CardContent, Paper, Stack, Typography } from "@mui/material";
import { StyledCard } from "../../assets/styled-components/StyledCard";
import { useNavigate } from "react-router-dom";

// Utility function to calculate luminance
const getLuminance = (hex) => {
  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;

  const a = [r, g, b].map(function (v) {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

// Determine text color based on luminance
const getTextColor = (bgColor) => {
  return getLuminance(bgColor) > 0.5 ? "#000" : "#fff";
};

// Updated function to extract work group data dynamically from response
const extractWorkGroups = (response) => {
  return Object.entries(response)
    .filter(([key]) => key.startsWith("workGroup"))
    .map(([, value]) => value);
};

const colorMap = [
  "#00251a", // Dark green (starting point, very dark)
  "#004d40", // Darker teal
  "#00695c", // Teal
  "#00796b", // Medium teal
  "#00897b", // Lighter teal
  "#009688", // Light teal
  "#26a69a", // Light aqua
  "#4db6ac", // Pale aqua
  "#80cbc4", // Light mint green
  "#b2dfdb", // Very light mint
  "#e0f2f1", // Pale mint/white
  "#f8f9fa", // White (lightest shade, to complete the gradient)
];

const AssetsByWorkGroup = ({ response }) => {
  const navigate = useNavigate();
  const workGroups = extractWorkGroups(response);
  const extractWorkGroupCode = (group) => {
    return group.desc.split(" - ")[0];
  };
  const handleCardClick = (code, service) => {
    if (service === "FEMS") {
      navigate("/assetsFEM", { state: { code: code, service: '/assetsFEM' } });
    } else {
      navigate("/assetsBEM", { state: { code: code, service: '/assetsBEM' } });
    }
  };
  return (
    <StyledCard variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            mb={1.5}
            variant="h5"
            fontWeight={700}
            color="text.primary"
          >
            Assets by Work Groups
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gridAutoRows: "1fr", // Ensures cards have equal height
              width: "100%",
            }}
          >
            {workGroups.map((group, index) => {
              const bgColor = colorMap[index % colorMap.length]; // Use modulo for color recycling
              const textColor = getTextColor(bgColor);
              const code = extractWorkGroupCode(group);
              return (
                <StyledCard
                  key={group.name}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleCardClick(code, group.service)} // Adding click event
                >
                  <Paper
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      width: "100%",
                      minWidth: "150px",
                      height: "100%", // Ensures the card fills the height
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {group.desc}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      {group.count}
                    </Typography>
                  </Paper>
                </StyledCard>
              );
            })}
          </Box>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default AssetsByWorkGroup;
